import { Module } from '../../student/types';

/**
 * Calculates the UI state of a module based on its workflow state and the student's progress.
 *
 * NEW LOGIC:
 * - If module is submitted or graded, return that state.
 * - Otherwise, determine if it belongs to the "current active chapter".
 * - The "current active chapter" is the first chapter that has at least one module not yet finished.
 * - All modules in the current chapter are 'active'.
 * - All modules in future chapters are 'locked'.
 * - If a course has no chapters (or only one), all modules are 'active'.
 *
 * @param module - The module to check
 * @param modules - All modules in the course (to determine chapter completion)
 * @returns The UI state: 'assigned' | 'submitted' | 'graded' | 'active' | 'locked'
 */
export const getModuleState = (module: Module, modules: Module[]): string => {
  const workflowState = module.state?.studentWorkflowState;

  // 1. Terminal states are always returned as is
  if (workflowState === 'submitted' || workflowState === 'graded') {
    return workflowState;
  }

  // 2. Identify the chapters (segments) from the first course found
  // Note: We assume all modules belong to the same primary course in this view
  const primaryCourse = module.courses?.[0];
  const segments = primaryCourse?.segments || [];

  // If no chapters defined, or only one, all modules are active
  if (!segments || segments.length <= 1) {
    return 'active';
  }

  // 3. Sort segments by sortIndex
  const sortedSegments = [...segments].sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0));

  // 4. Find the current active chapter
  // A chapter is "finished" if all its modules are submitted or graded
  let currentChapterId = sortedSegments[0].id;

  for (const segment of sortedSegments) {
    const chapterModules = modules.filter((m) => m.courseSettings?.segmentId === segment.id);

    // If there are modules in this chapter, check them
    if (chapterModules.length > 0) {
      const hasUnfinishedModule = chapterModules.some(
        (m) => m.state?.studentWorkflowState !== 'submitted' && m.state?.studentWorkflowState !== 'graded',
      );

      if (hasUnfinishedModule) {
        currentChapterId = segment.id;
        break;
      }
      // If all modules in this chapter are finished, the next one is the candidate
      currentChapterId = segment.id;
    }
  }

  // 5. Determine if the module's chapter is the current one or a future one
  const moduleChapterId = module.courseSettings?.segmentId;

  // If module has no chapter assigned, we treat it as active (fallback)
  if (!moduleChapterId) {
    return 'active';
  }

  const currentChapterIndex = sortedSegments.findIndex((s) => s.id === currentChapterId);
  const moduleChapterIndex = sortedSegments.findIndex((s) => s.id === moduleChapterId);

  if (moduleChapterIndex > currentChapterIndex) {
    return 'locked';
  }

  return 'active';
};

export default getModuleState;
